<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import dayjs from "dayjs";
import apiClient from "@/apiClient";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import { useAuthStore } from "@/stores/AuthStore";
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  Cog6ToothIcon,
  MapPinIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";

useBreadcrumbs().setBreadcrumb("365");
const auth = useAuthStore();

const journeys = ref([]);
const loading = ref(true);
const checking = ref(false);
const savingSettings = ref(false);
const syncingJourneys = ref(false);
const biddingId = ref(null);
const message = ref("");
const error = ref("");
const now = ref(dayjs());
const showPricingModal = ref(false);
const showSyncModal = ref(false);
const serverCanManagePricing = ref(false);
let clock = null;

const settings = reactive({
  base_address: "Llangefni, Isle of Anglesey, UK",
  hourly_rate: null,
  waiting_hourly_rate: null,
  minimum_duration_hours: 0,
});
const syncPeriod = reactive({
  start_date: dayjs().startOf("year").format("YYYY-MM-DD"),
  end_date: dayjs().format("YYYY-MM-DD"),
});

const currency = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

const activeCount = computed(
  () => journeys.value.filter((journey) => !isExpired(journey)).length,
);
const bidCount = computed(
  () =>
    journeys.value.filter(
      (journey) => journey.status === "bid" && !isExpired(journey),
    ).length,
);
const canManagePricing = computed(
  () => serverCanManagePricing.value && auth.hasRole("super"),
);
const lastChecked = computed(() => {
  const timestamps = journeys.value
    .map((journey) => journey.last_seen_at)
    .filter(Boolean)
    .map((value) => dayjs(value).valueOf());
  return timestamps.length ? dayjs(Math.max(...timestamps)) : null;
});

const applyPayload = (payload = {}) => {
  journeys.value = Array.isArray(payload.journeys) ? payload.journeys : [];
  serverCanManagePricing.value = Boolean(payload.can_manage_pricing);
  if (payload.settings) Object.assign(settings, payload.settings);
};

const load = async () => {
  loading.value = true;
  error.value = "";
  try {
    const response = await apiClient.get("/api/operations/platform365", {
      suppressErrorAlert: true,
    });
    applyPayload(response.data);
  } catch (requestError) {
    error.value =
      requestError.response?.data?.message ||
      "The 365 app could not be loaded.";
  } finally {
    loading.value = false;
  }
};

const checkForJourneys = async () => {
  checking.value = true;
  message.value = "";
  error.value = "";
  try {
    const response = await apiClient.post(
      "/api/operations/platform365/check",
      {},
      {
        suppressErrorAlert: true,
      },
    );
    applyPayload(response.data);
    const found = Number(response.data?.check?.found || 0);
    message.value = `${found} available ${found === 1 ? "journey" : "journeys"} found.`;
  } catch (requestError) {
    error.value =
      requestError.response?.data?.message ||
      "Platform 365 could not be checked.";
  } finally {
    checking.value = false;
  }
};

const saveSettings = async () => {
  savingSettings.value = true;
  message.value = "";
  error.value = "";
  try {
    const response = await apiClient.put(
      "/api/operations/platform365/settings",
      {
        base_address: settings.base_address,
        hourly_rate: Number(settings.hourly_rate),
        waiting_hourly_rate: Number(settings.waiting_hourly_rate),
        minimum_duration_hours: Number(settings.minimum_duration_hours),
      },
      { suppressErrorAlert: true },
    );
    Object.assign(settings, response.data);
    journeys.value = journeys.value.map((journey) =>
      ["available", "bid"].includes(journey.status)
        ? {
            ...journey,
            suggested_bid: null,
            calculated_duration_minutes: null,
            pricing_error:
              "Check for journeys to calculate a bid with the updated settings.",
            can_bid: false,
          }
        : journey,
    );
    message.value =
      "Pricing settings saved. Check for journeys to refresh suggested bids.";
    showPricingModal.value = false;
  } catch (requestError) {
    const validation = requestError.response?.data?.errors;
    error.value = validation
      ? Object.values(validation).flat()[0]
      : requestError.response?.data?.message ||
        "The pricing settings could not be saved.";
  } finally {
    savingSettings.value = false;
  }
};

const syncCompletedJourneys = async () => {
  syncingJourneys.value = true;
  message.value = "";
  error.value = "";
  try {
    const response = await apiClient.post(
      "/api/operations/platform365/sync",
      {
        start_date: syncPeriod.start_date,
        end_date: syncPeriod.end_date,
      },
      { suppressErrorAlert: true },
    );
    const result = response.data?.sync ?? {};
    message.value = `${Number(result.created || 0)} journey${Number(result.created || 0) === 1 ? "" : "s"} created; ${Number(result.existing || 0)} already on Neptune.`;
    showSyncModal.value = false;
  } catch (requestError) {
    const validation = requestError.response?.data?.errors;
    error.value = validation
      ? Object.values(validation).flat()[0]
      : requestError.response?.data?.message ||
        "Completed journeys could not be synced.";
  } finally {
    syncingJourneys.value = false;
  }
};

const submitBid = async (journey) => {
  if (!journey.can_bid || isExpired(journey)) return;
  const amount = formatCurrency(journey.suggested_bid);
  if (
    !window.confirm(
      `Submit a ${amount} bid for journey ${journey.external_journey_id}? The journey and price will be checked again first.`,
    )
  ) {
    return;
  }

  biddingId.value = journey.id;
  message.value = "";
  error.value = "";
  try {
    const response = await apiClient.post(
      `/api/operations/platform365/journeys/${journey.id}/bid`,
      {},
      { suppressErrorAlert: true },
    );
    const index = journeys.value.findIndex((item) => item.id === journey.id);
    if (index !== -1) journeys.value[index] = response.data.journey;
    message.value = response.data.message || "Bid submitted to Platform 365.";
  } catch (requestError) {
    const bidError =
      requestError.response?.data?.message || "The bid could not be submitted.";
    if (requestError.response?.status === 409) await load();
    error.value = bidError;
  } finally {
    biddingId.value = null;
  }
};

const isExpired = (journey) =>
  journey.status === "expired" ||
  (journey.expires_at &&
    dayjs(journey.expires_at).valueOf() <= now.value.valueOf());

const expiresLabel = (journey) => {
  if (!journey.expires_at) return "Expiry unavailable";
  if (isExpired(journey)) return "Expired";
  const minutes = Math.max(
    dayjs(journey.expires_at).diff(now.value, "minute"),
    0,
  );
  if (minutes < 60) return `Expires in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `Expires in ${hours}h ${minutes % 60}m`;
};

const formatDate = (value) =>
  value ? dayjs(value).format("ddd D MMM, HH:mm") : "Not provided";
const formatCurrency = (value) =>
  value === null || value === undefined ? "—" : currency.format(Number(value));
const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return "—";
  const hours = Math.floor(Number(minutes) / 60);
  const remainder = Number(minutes) % 60;
  return (
    [hours ? `${hours}h` : "", remainder ? `${remainder}m` : ""]
      .filter(Boolean)
      .join(" ") || "0m"
  );
};
const statusClasses = (journey) => {
  if (isExpired(journey))
    return "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300";
  if (journey.status === "bid")
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300";
  return "bg-cyan-100 text-cyan-700 dark:bg-cyan-950/50 dark:text-cyan-300";
};

onMounted(() => {
  load();
  clock = window.setInterval(() => {
    now.value = dayjs();
  }, 30000);
});
onUnmounted(() => window.clearInterval(clock));
</script>

<template>
  <div class="space-y-6 pb-12">
    <header class="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p
          class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
        >
          Operations marketplace
        </p>
        <h1
          class="mt-1 text-2xl font-semibold text-slate-900 dark:text-slate-100"
        >
          365
        </h1>
        <p class="mt-1 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
          Check available journeys, review round-trip pricing from Llangefni,
          and submit verified bids.
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          v-if="canManagePricing"
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          @click="showSyncModal = true"
        >
          <ArrowDownTrayIcon class="h-5 w-5" /> Sync journeys
        </button>
        <button
          v-if="canManagePricing"
          type="button"
          class="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          @click="showPricingModal = true"
        >
          <Cog6ToothIcon class="h-5 w-5" /> Bid pricing
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="checking"
          @click="checkForJourneys"
        >
          <ArrowPathIcon
            class="h-5 w-5"
            :class="{ 'animate-spin': checking }"
          />
          {{ checking ? "Checking 365…" : "Check for journeys" }}
        </button>
      </div>
    </header>

    <div
      v-if="message"
      class="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
    >
      {{ message }}
    </div>
    <div
      v-if="error"
      class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-200"
    >
      {{ error }}
    </div>

    <div>
      <main class="min-w-0 space-y-4">
        <section class="grid gap-3 sm:grid-cols-3">
          <div
            class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <p
              class="text-xs font-medium uppercase tracking-wide text-slate-400"
            >
              Available
            </p>
            <p
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ activeCount }}
            </p>
          </div>
          <div
            class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <p
              class="text-xs font-medium uppercase tracking-wide text-slate-400"
            >
              Bids placed
            </p>
            <p
              class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
            >
              {{ bidCount }}
            </p>
          </div>
          <div
            class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800"
          >
            <p
              class="text-xs font-medium uppercase tracking-wide text-slate-400"
            >
              Last checked
            </p>
            <p
              class="mt-2 text-sm font-semibold text-slate-900 dark:text-white"
            >
              {{
                lastChecked
                  ? lastChecked.format("D MMM, HH:mm")
                  : "Not checked yet"
              }}
            </p>
          </div>
        </section>

        <div
          v-if="loading"
          class="rounded-xl border border-slate-200 bg-white py-16 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800"
        >
          Loading journeys…
        </div>
        <div
          v-else-if="!journeys.length"
          class="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center dark:border-slate-600 dark:bg-slate-800"
        >
          <ArrowPathIcon class="mx-auto h-8 w-8 text-slate-300" />
          <p class="mt-3 font-medium text-slate-700 dark:text-slate-200">
            No journeys have been checked yet
          </p>
          <p class="mt-1 text-sm text-slate-500">
            Check Platform 365 to load the latest available journeys.
          </p>
        </div>

        <template v-else>
          <article
            v-for="journey in journeys"
            :key="journey.id"
            class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800"
            :class="{ 'opacity-70': isExpired(journey) }"
          >
            <div
              class="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-700"
            >
              <div>
                <div class="flex flex-wrap items-center gap-2">
                  <h2
                    class="text-lg font-semibold text-slate-900 dark:text-white"
                  >
                    Journey {{ journey.external_journey_id }}
                  </h2>
                  <span
                    class="rounded-full px-2.5 py-1 text-xs font-semibold"
                    :class="statusClasses(journey)"
                  >
                    {{
                      isExpired(journey)
                        ? "Expired"
                        : journey.status === "bid"
                          ? "Bid placed"
                          : "Available"
                    }}
                  </span>
                </div>
                <p class="mt-1 text-sm text-slate-500">
                  {{ journey.journey_type || "Journey" }} ·
                  {{ journey.service || "Service not provided" }}
                </p>
              </div>
              <div class="text-right">
                <p
                  class="text-sm font-semibold"
                  :class="
                    isExpired(journey)
                      ? 'text-slate-500'
                      : 'text-amber-600 dark:text-amber-300'
                  "
                >
                  {{ expiresLabel(journey) }}
                </p>
                <p
                  v-if="journey.expires_at"
                  class="mt-0.5 text-xs text-slate-400"
                >
                  {{ formatDate(journey.expires_at) }}
                </p>
              </div>
            </div>

            <div
              class="grid gap-5 px-5 py-5 lg:grid-cols-[minmax(0,1fr)_15rem]"
            >
              <div class="space-y-4">
                <div class="grid gap-3 sm:grid-cols-2">
                  <div
                    class="flex gap-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40"
                  >
                    <MapPinIcon class="mt-0.5 h-5 w-5 shrink-0 text-cyan-600" />
                    <div>
                      <p
                        class="text-xs font-medium uppercase tracking-wide text-slate-400"
                      >
                        Pickup
                      </p>
                      <p
                        class="mt-1 text-sm text-slate-700 dark:text-slate-200"
                      >
                        {{ journey.pickup_address || "Not provided" }}
                      </p>
                    </div>
                  </div>
                  <div
                    class="flex gap-3 rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40"
                  >
                    <MapPinIcon
                      class="mt-0.5 h-5 w-5 shrink-0 text-indigo-500"
                    />
                    <div>
                      <p
                        class="text-xs font-medium uppercase tracking-wide text-slate-400"
                      >
                        Drop-off
                      </p>
                      <p
                        class="mt-1 text-sm text-slate-700 dark:text-slate-200"
                      >
                        {{ journey.dropoff_address || "Not provided" }}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  class="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 dark:text-slate-300"
                >
                  <span
                    ><strong
                      class="font-medium text-slate-800 dark:text-slate-100"
                      >Appointment:</strong
                    >
                    {{ formatDate(journey.appointment_at) }}</span
                  >
                  <span
                    ><strong
                      class="font-medium text-slate-800 dark:text-slate-100"
                      >Pickup:</strong
                    >
                    {{ formatDate(journey.pickup_at) }}</span
                  >
                  <span v-if="journey.mobility"
                    ><strong
                      class="font-medium text-slate-800 dark:text-slate-100"
                      >Mobility:</strong
                    >
                    {{ journey.mobility }}</span
                  >
                </div>
                <p
                  v-if="journey.pricing_error"
                  class="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                >
                  {{ journey.pricing_error }}
                </p>
              </div>

              <aside
                class="rounded-lg border border-slate-200 p-4 dark:border-slate-600"
              >
                <p
                  class="text-xs font-medium uppercase tracking-wide text-slate-400"
                >
                  Suggested bid
                </p>
                <p
                  class="mt-1 text-2xl font-semibold text-slate-900 dark:text-white"
                >
                  {{ formatCurrency(journey.suggested_bid) }}
                </p>
                <p class="mt-1 text-xs text-slate-500">
                  {{ formatDuration(journey.calculated_duration_minutes) }}
                  chargeable duration
                </p>
                <p
                  v-if="journey.bid_amount !== null"
                  class="mt-3 flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300"
                >
                  <CheckCircleIcon class="h-4 w-4" /> Current bid
                  {{ formatCurrency(journey.bid_amount) }}
                </p>
                <button
                  type="button"
                  class="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-cyan-600 dark:hover:bg-cyan-700"
                  :disabled="
                    !journey.can_bid ||
                    isExpired(journey) ||
                    biddingId === journey.id
                  "
                  @click="submitBid(journey)"
                >
                  <BanknotesIcon class="h-4 w-4" />
                  {{
                    biddingId === journey.id
                      ? "Checking and bidding…"
                      : journey.status === "bid"
                        ? "Update bid"
                        : "Bid on journey"
                  }}
                </button>
              </aside>
            </div>
          </article>
        </template>
      </main>
    </div>

    <Teleport to="body">
      <div
        v-if="showPricingModal && canManagePricing"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform365-pricing-title"
        @click.self="showPricingModal = false"
      >
        <section
          class="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2
                id="platform365-pricing-title"
                class="text-lg font-semibold text-slate-900 dark:text-white"
              >
                Bid pricing
              </h2>
              <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Super-user settings for journey and additional waiting charges.
              </p>
            </div>
            <button
              type="button"
              class="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
              aria-label="Close pricing settings"
              @click="showPricingModal = false"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <form class="mt-6 space-y-4" @submit.prevent="saveSettings">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Dispatch base
              <input
                v-model.trim="settings.base_address"
                type="text"
                required
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Journey hourly price (£)
              <input
                v-model.number="settings.hourly_rate"
                type="number"
                min="0.01"
                max="10000"
                step="0.01"
                required
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Waiting hourly price (£)
              <input
                v-model.number="settings.waiting_hourly_rate"
                type="number"
                min="0.01"
                max="10000"
                step="0.01"
                required
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              />
              <span class="mt-1 block text-xs font-normal text-slate-500">
                Sent to 365 as the additional hourly waiting charge.
              </span>
            </label>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Minimum duration (hours)
              <input
                v-model.number="settings.minimum_duration_hours"
                type="number"
                min="0"
                max="48"
                step="0.25"
                required
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <div class="flex justify-end gap-2 pt-2">
              <button
                type="button"
                class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700"
                @click="showPricingModal = false"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="savingSettings"
                class="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-60"
              >
                <ClockIcon class="h-4 w-4" />
                {{ savingSettings ? "Saving…" : "Save pricing" }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showSyncModal && canManagePricing"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="platform365-sync-title"
        @click.self="showSyncModal = false"
      >
        <section
          class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-800"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2
                id="platform365-sync-title"
                class="text-lg font-semibold text-slate-900 dark:text-white"
              >
                Sync completed journeys
              </h2>
              <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Create missing M1SJ references directly from the completed 365
                journey grid.
              </p>
            </div>
            <button
              type="button"
              class="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-white"
              aria-label="Close journey sync"
              @click="showSyncModal = false"
            >
              <XMarkIcon class="h-5 w-5" />
            </button>
          </div>

          <form class="mt-6 space-y-4" @submit.prevent="syncCompletedJourneys">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Start date
              <input
                v-model="syncPeriod.start_date"
                type="date"
                required
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              End date
              <input
                v-model="syncPeriod.end_date"
                type="date"
                :min="syncPeriod.start_date"
                required
                class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
              />
            </label>
            <div class="flex justify-end gap-2 pt-2">
              <button
                type="button"
                class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-700"
                @click="showSyncModal = false"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="syncingJourneys"
                class="inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 disabled:opacity-60"
              >
                <ArrowPathIcon
                  class="h-4 w-4"
                  :class="{ 'animate-spin': syncingJourneys }"
                />
                {{ syncingJourneys ? "Syncing…" : "Sync journeys" }}
              </button>
            </div>
          </form>
        </section>
      </div>
    </Teleport>
  </div>
</template>
